import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHandlerFn,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { SessionStorageService } from '../services/session-storage.service';
import { SessionStorage } from '../services/constant.service';
import { UtilService } from '../services/util.service';
import { TokenService } from '../services/token.service';


export function ApiInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const tokenService = inject(TokenService);
  const sessionStorageService = inject(SessionStorageService);
  const utilService = inject(UtilService);
  const appSource: string =
    sessionStorageService.getItem(SessionStorage.appSource) || '';
  const clientCode: string =
    sessionStorageService.getItem(SessionStorage.clientCode) || '';
  const userMobile: string = '';

  const tokenUrls = [
    `${environment.kyc_Url}auth/generate/key`, // KYC token generation
    // `${environment.diygateway_url}GenerateToken`, // eAccount token generation
    // `${environment.ipgeolocation}ipgeo`,
    `https://gateway.5paisa.com/tradeapi/CheckLoginResponse`,
    'https://gateway.5paisa.com/AuthApi/ClientLoginRequest',
    'https://gateway.5paisa.in/ModificationAPI/JWTToken'
  ];


const isKycOrOtherTokenUrl = tokenUrls.some((url) => req.url.includes(url));

const isCheckLoginWithClientCode =
  req.url.startsWith('https://gateway.5paisa.com/AuthApi/ClientLoginRequest') &&
  req.url.includes('clientCode=') &&
  !!new URL(req.url).searchParams.get('clientCode');

  if (tokenUrls.some((url) => req.url.includes(url))) {
  // if (isKycOrOtherTokenUrl) {
    return next(req);
  }

  const tokenMap: { [url: string]: string[] } = {
    [environment.kyc_Url]: [
      sessionStorageService.getItem(SessionStorage.authenticationkey_KYC) || '',
      sessionStorageService.getItem(SessionStorage.authorization_KYC) || '',
    ],
    [environment.baseModificationAPI]: [
      sessionStorageService.getItem(SessionStorage.authorization_Modi) || '',
      sessionStorageService.getItem(SessionStorage.authorization_Modi) || ''
    ],
  };

  let authToken = '';
  let authKey = '';

  // Find the appropriate tokens for the current request URL
  Object.keys(tokenMap).forEach((baseUrl) => {
    if (req.url.startsWith(baseUrl)) {
      authToken = tokenMap[baseUrl][0]; // Authorization token
      authKey = tokenMap[baseUrl][1]; // Authentication key
    }
  });

  // const authReq = req.clone({
  //   setHeaders: {
  //     Authorization: authToken,
  //     AuthenticationKey: authKey,
  //   },
  // });

  const authReq = req.clone({
    setHeaders: {
      ...(authToken ? { AuthenticationKey: authToken } : {}),
      ...(authKey ? { Authorization: authKey} : {}),
      // 'Ocp-Apim-Subscription-Key': '2284d4215515451ca7266097726d8097',
    },
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status === 401 ||
        error.status === 403 ||
        // error.status === 500 ||
        error.status == 0
      ) {
        if (error.url?.includes(environment.kyc_Url)) {
          return tokenService
            .auth_Token_KYC({
              appSource: appSource,
              mobile: userMobile,
              clientCode: clientCode,
            })
            .pipe(
              switchMap((newToken) => {
                console.log('authenticationkey_KYC-->', newToken);
                sessionStorageService.setItem(
                  SessionStorage.authenticationkey_KYC,
                  newToken.data.token
                );
                const newAuthReq = req.clone({
                  setHeaders: {
                    AuthenticationKey: newToken.data.token,
                    Authorization: tokenMap[environment.kyc_Url][1],
                   },
                });
                return next(newAuthReq);
              }),
              catchError((err) => {
                return handleSecondaryError(
                  err,
                  req,
                  next,
                  tokenService,
                  sessionStorageService,
                  appSource,
                  userMobile,
                  clientCode
                );
              })
            );
        } 
      } else if (error.status === 422) {
        if (error.error.msg == 'Authorization Header is missing or invalid')
          if (error.url?.includes(environment.kyc_Url)) {
            return tokenService.generateToken_KYC(appSource).pipe(
              switchMap((newToken) => {
                sessionStorageService.setItem(
                  SessionStorage.authorization_KYC,
                  newToken.data.token
                );
                const newAuthReq = req.clone({
                  setHeaders: { Authorization: newToken.data.token },
                });
                return next(newAuthReq);
              }),
              catchError((err) => {
                return handleSecondaryError(
                  err,
                  req,
                  next,
                  tokenService,
                  sessionStorageService,
                  appSource,
                  userMobile,
                  clientCode
                );
              })
            );
          }
      }
      // If the error is not 401/403, just pass it through
      return throwError(() => error);
    })
  );
}

// Secondary Error Handling Function
function handleSecondaryError(
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  tokenService: TokenService,
  sessionStorageService: SessionStorageService,
  appSource: string,
  userMobile: string,
  clientCode: string
): Observable<any> {
  if (error.status === 401) {
    console.log('401 error occurred, retrying KYC token');

    // Handle 401 for KYC URL
    if (error.url?.includes(environment.kyc_Url)) {
      return tokenService
        .auth_Token_KYC({
          appSource: appSource,
          mobile: userMobile,
          clientCode: clientCode,
        }).pipe(
          switchMap((newToken) => {
            console.log('New KYC Token:', newToken);
            sessionStorageService.setItem(
              SessionStorage.authenticationkey_KYC,
              newToken.data.token
            );
            const authorization = sessionStorageService.getItem(
              SessionStorage.authorization_KYC
            );
            const newAuthReq = req.clone({
              setHeaders: {
                AuthenticationKey: newToken.data.token,
                authorization: authorization || '',
              },
            });
            return next(newAuthReq); // Retry the original request
          })
        );
    }
  } else if (error.status === 422) {
    // if (error.msg == 'Authorization Header is missing or invalid')
    if (error.error.msg == 'Authorization Header is missing or invalid')
      if (error.url?.includes(environment.kyc_Url)) {
        // Handle KYC token renewal
        return tokenService.generateToken_KYC(appSource).pipe(
          switchMap((newToken) => {
            console.log('authorization_KYC-->', newToken);
            // Save new token in localStorage (or a service)
            sessionStorageService.setItem(
              SessionStorage.authorization_KYC,
              newToken.data.token
            );
            // Retry the original request with the new token
            const newAuthReq = req.clone({
              setHeaders: {
                Authorization: newToken.data.token,
              },
            });
            return next(newAuthReq);
          })
        );
      }
  }
  return throwError(() => error);
}
